import { t } from 'i18next';

import type { StudioPlanId } from 'src/services/api/studioCreditApi';

/**
 * Studio features gated by plan under Studio pricing (POR-21135). Keys are
 * consumed by the per-tier surface gates (POR-21371/21410/21409) and the
 * upgrade badges (POR-21137) via the `studio` section of `usePlanStatus`.
 */
export type StudioFeature =
  | 'customDomain'
  | 'api'
  | 'multiCompanyClients'
  | 'automations'
  | 'removeBranding'
  | 'modelSelection'
  | 'auditLog'
  | 'clientAccessPermissions'
  | 'enforceMfa';

/**
 * Plan hierarchy. A plan is entitled to a feature when its rank is at least the
 * feature's minimum-plan rank, so the values only need to preserve the ordering
 * freemium < starter < professional < advanced.
 *
 * Backend `planRank` in backend/lib/services/stripe_cli/plans/plan_change.go is
 * the source of truth for plan ordering; this mirrors it and the two must stay
 * in sync. There is no Go↔TS codegen for the constant, and feature gating needs
 * the ordering synchronously (no network round-trip), so the copy is deliberate
 * — kept structurally identical to the backend map so a drift is easy to spot.
 */
export const STUDIO_PLAN_RANK: Record<StudioPlanId, number> = {
  freemium: 0,
  starter: 1,
  professional: 2,
  advanced: 3,
};

/**
 * PRD entitlement matrix: the lowest plan that unlocks each feature. This is the
 * single source of truth — surfaces branch on {@link planHasFeatureAccess} rather
 * than re-encoding tiers.
 */
export const STUDIO_FEATURE_MIN_PLAN: Record<StudioFeature, StudioPlanId> = {
  customDomain: 'starter',
  api: 'starter',
  multiCompanyClients: 'professional',
  automations: 'professional',
  removeBranding: 'professional',
  modelSelection: 'professional',
  auditLog: 'advanced',
  clientAccessPermissions: 'advanced',
  enforceMfa: 'advanced',
};

export const getStudioPlanRank = (plan: StudioPlanId): number =>
  STUDIO_PLAN_RANK[plan];

export const requiredPlanForFeature = (feature: StudioFeature): StudioPlanId =>
  STUDIO_FEATURE_MIN_PLAN[feature];

export const planHasFeatureAccess = (
  plan: StudioPlanId,
  feature: StudioFeature,
): boolean =>
  getStudioPlanRank(plan) >= getStudioPlanRank(requiredPlanForFeature(feature));

/** Placeholder rendered when a value (e.g. an unresolved plan) is unavailable. */
export const EMPTY_VALUE = '—';

/**
 * Display title for a Studio plan, sourced from the Plans-page tier strings so a
 * plan renders the same name across the Plans page, the current-plan summary,
 * and entitlement upgrade badges. Note the `professional` tier renders as "Pro".
 * Returns {@link EMPTY_VALUE} when the plan is unresolved.
 */
export const getStudioPlanTitle = (plan?: StudioPlanId): string => {
  switch (plan) {
    case 'freemium':
      return t(($) => $.page_studio_plans.tiers.freemium.title);
    case 'starter':
      return t(($) => $.page_studio_plans.tiers.starter.title);
    case 'professional':
      return t(($) => $.page_studio_plans.tiers.pro.title);
    case 'advanced':
      return t(($) => $.page_studio_plans.tiers.advanced.title);
    default:
      return EMPTY_VALUE;
  }
};
